import { OrganizationType } from "@remi/enums/organization-type";
import { isOneOf } from "@remi/ts-utils";
import type { PermissionRole } from "@remi/types/permissions";

import { hasCapability, type User } from "../base";
import { canRoleManageRole } from "../utils/invitation";
import { REMI_ORG_RESTRICTED_ROLES } from "../utils/remi-org-restricted-roles";
import { OrganizationTypeToPermissionRoles } from "../utils/types";

const REMIHQ_EMAIL_SUFFIX = "@remihq.com";

type ActorWithOrganizationType = User & {
  person: { organization?: { type: OrganizationType } };
};

export const person = {
  update: (user: User, personId: string) => {
    return hasCapability(user, "person.update.all") || user.person.id === personId;
  },
  updateInOrg: (user: User, target: { id: string; organizationId: string }) => {
    if (hasCapability(user, "person.update.all") || user.person.id === target.id) {
      return true;
    }
    return hasCapability(user, "person.update.org") && user.person.organizationId === target.organizationId;
  },
  create: (
    user: ActorWithOrganizationType,
    userToCreate: {
      organizationId: string;
      organizationType: OrganizationType;
      roles: PermissionRole[];
      inviteeEmail?: string | null;
    },
  ) => {
    if (
      userToCreate.organizationType === OrganizationType.REMI_INTERNAL &&
      userToCreate.inviteeEmail !== undefined &&
      userToCreate.inviteeEmail !== null &&
      !userToCreate.inviteeEmail.toLowerCase().endsWith(REMIHQ_EMAIL_SUFFIX)
    ) {
      return false;
    }

    const orgRestrictedRoles = userToCreate.roles.filter((role) => isOneOf(role, REMI_ORG_RESTRICTED_ROLES));

    if (hasCapability(user, "permission.write")) {
      return orgRestrictedRoles.length === 0 || userToCreate.organizationType === OrganizationType.REMI_INTERNAL;
    }

    // REMI_USER is the only org-restricted role non-managers may invite (Remi staff onboarding).
    if (orgRestrictedRoles.some((role) => role !== "REMI_USER")) {
      return false;
    }
    if (orgRestrictedRoles.length > 0) {
      const actorIsRemiInternal = user.person.organization?.type === OrganizationType.REMI_INTERNAL;
      // null = server path with no email; deny so REMI_USER can't be granted without @remihq.com verification.
      const hasVerifiedEmail = userToCreate.inviteeEmail !== null;
      if (!actorIsRemiInternal || !hasVerifiedEmail) {
        return false;
      }
    }

    const validRolesForOrg = new Set(OrganizationTypeToPermissionRoles[userToCreate.organizationType]);
    if (!userToCreate.roles.every((role) => validRolesForOrg.has(role))) {
      return false;
    }

    if (!hasCapability(user, "person.create") && user.person.organizationId !== userToCreate.organizationId) {
      return false;
    }

    if (!hasCapability(user, "person.invite")) {
      return false;
    }

    return userToCreate.roles.every((target) =>
      user.person.permissionRoles.some((granter) => canRoleManageRole(granter, target)),
    );
  },
  delete: (user: User, userToDelete: { organizationId: string }) => {
    return hasCapability(user, "person.delete") || user.person.organizationId === userToDelete.organizationId;
  },
  readBasic: (user: User) => hasCapability(user, "person.read.basic"),
  invite: (user: User) => hasCapability(user, "person.invite"),
  viewDetails: (user: User) => hasCapability(user, "person.viewDetails"),
  viewContactDetails: (user: User, person: { id: string; organizationId: string }) =>
    hasCapability(user, "person.viewDetails") ||
    user.person.id === person.id ||
    user.person.organizationId === person.organizationId,
  viewUnmaskedPhoneNumber: (
    user: User,
    personId: string,
    personOrganization: { type: OrganizationType; id: string },
    isProxyEnabledForContractor: boolean,
  ) => {
    if (
      personOrganization.type !== OrganizationType.CONTRACTOR &&
      personOrganization.type !== OrganizationType.HOMEOWNER
    ) {
      return true;
    }
    if (!isProxyEnabledForContractor) {
      return true;
    }
    return (
      hasCapability(user, "person.viewUnmaskedPhoneNumber") ||
      user.person.id === personId ||
      user.person.organizationId === personOrganization.id
    );
  },
  // Temporary: gates proxy number visibility during beta rollout. Merges into viewUnmaskedPhoneNumber when proxy is GA.
  viewProxyPhoneNumber: (
    user: User,
    personId: string,
    personOrganization: { type: OrganizationType; id: string },
    isProxyEnabledForContractor: boolean,
  ) => {
    if (hasCapability(user, "person.viewProxyPhoneNumber")) {
      return true;
    }
    if (isProxyEnabledForContractor) {
      return user.person.id === personId || user.person.organizationId === personOrganization.id;
    }
    return false;
  },
  changeOrganization: (user: User) => hasCapability(user, "person.changeOrganization"),
  createPerson: (user: User) => hasCapability(user, "person.createPerson"),
};
